import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { listarUsuarios } from '../../usuarios/api';
import { listProposals } from '../../propostas-comerciais/api';
import { listarClientes, type ClienteListItem } from '../../clientes/api';
import type { CreateContractPayload } from '../api';
import { usePreviewGeracaoContrato } from '../hooks';
import { ProposalOrigemBanner } from './ProposalOrigemBanner';

type Tab = 'identificacao' | 'manual';

const MOEDA_OPTIONS = [
  { value: 'BRL', label: 'BRL — Real' },
  { value: 'USD', label: 'USD — Dólar' },
  { value: 'EUR', label: 'EUR — Euro' },
];

interface ContractFormErrors {
  numero?: string;
  clientId?: string;
  responsavelComercialId?: string;
  gestorContratoId?: string;
  vigenciaInicial?: string;
  vigenciaFinal?: string;
  justificativaManual?: string;
}

interface ContractFormProps {
  initialValues?: Partial<CreateContractPayload>;
  onSubmit: (values: CreateContractPayload) => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  /** Erro vindo da API (ex.: 422 com pré-condições não atendidas). */
  externalError?: React.ReactNode;
}

function validate(values: Partial<CreateContractPayload>): ContractFormErrors {
  const errors: ContractFormErrors = {};
  if (!values.numero?.trim()) errors.numero = 'Número é obrigatório.';
  if (!values.clientId?.trim()) errors.clientId = 'Cliente é obrigatório.';
  if (!values.responsavelComercialId?.trim()) errors.responsavelComercialId = 'Responsável comercial é obrigatório.';
  if (!values.gestorContratoId?.trim()) errors.gestorContratoId = 'Gestor do contrato é obrigatório.';
  if (!values.vigenciaInicial?.trim()) errors.vigenciaInicial = 'Vigência inicial é obrigatória.';
  if (!values.vigenciaFinal?.trim()) errors.vigenciaFinal = 'Vigência final é obrigatória.';
  if (!values.proposalId?.trim()) {
    if (!values.justificativaManual || values.justificativaManual.trim().length < 20) {
      errors.justificativaManual = 'Justificativa deve ter ao menos 20 caracteres para contratos sem proposta.';
    }
  }
  return errors;
}

export default function ContractForm({
  initialValues = {},
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Salvar',
  externalError,
}: ContractFormProps): React.ReactElement {
  const [values, setValues] = useState<Partial<CreateContractPayload>>({
    moeda: 'BRL',
    isRecorrente: false,
    ...initialValues,
  });
  const [errors, setErrors] = useState<ContractFormErrors>({});
  const [tab, setTab] = useState<Tab>('identificacao');

  const set = (field: keyof CreateContractPayload, value: unknown) => {
    markTouched(field as string);
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof ContractFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Autocomplete usa client-side filter; carregamos até 200 registros por
  // catálogo, seguindo o mesmo padrão de OpportunityForm/CommercialBaseSelect.
  const { data: clientesResp, isLoading: isLoadingClientes } = useQuery({
    queryKey: ['picker', 'clientes'],
    queryFn: () => listarClientes({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: usuariosResp, isLoading: isLoadingUsuarios } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: propostasResp, isLoading: isLoadingPropostas } = useQuery({
    queryKey: ['picker', 'propostas', values.clientId],
    queryFn: () => listProposals({ clientId: values.clientId, limit: 200 }),
    enabled: !!values.clientId,
    staleTime: 30 * 1000,
  });

  const clienteOptions = useMemo(
    () =>
      (clientesResp?.data ?? []).map((c) => ({
        value: c.id,
        label: c.razaoSocial,
        description: `${c.code} • ${c.documento}`,
      })),
    [clientesResp],
  );

  const usuarioOptions = useMemo(
    () =>
      (usuariosResp?.data ?? []).map((u) => ({
        value: u.id,
        label: u.name,
        description: u.email,
      })),
    [usuariosResp],
  );

  const propostaOptions = useMemo(
    () =>
      (propostasResp?.data ?? []).map((p) => ({
        value: p.id,
        label: p.codigo,
        description: p.currentVersion ? `Versão ${p.currentVersion.versionNumber} • ${p.currentVersion.status}` : undefined,
      })),
    [propostasResp],
  );

  // RN-COM-003 — o que a proposta traz e o que ainda falta. A condição do
  // cliente é reportada junto para que a lista de bloqueios venha completa.
  const selectedCliente = useMemo(
    () => (clientesResp?.data ?? []).find((c) => c.id === values.clientId),
    [clientesResp, values.clientId],
  );
  const clientConditions = useMemo(
    () =>
      selectedCliente &&
      selectedCliente.situacaoCadastral !== 'ativo' &&
      selectedCliente.situacaoCadastral !== 'em_cadastro'
        ? [
            {
              field: 'cliente.situacaoCadastral',
              label: 'Cliente ativo',
              ok: false,
              actual: selectedCliente.situacaoCadastral,
            },
          ]
        : [],
    [selectedCliente],
  );

  const { data: preview, isFetching: isPreviewFetching } = usePreviewGeracaoContrato(
    values.proposalId,
    clientConditions,
  );

  // Campos que o usuário não tocou recebem o valor da proposta. Os que ele
  // já preencheu ficam intactos — a mesma regra aplicada no backend.
  const touched = useRef(new Set<string>());
  const initialKeys = useRef(new Set(Object.keys(initialValues).filter((k) => {
    const v = (initialValues as Record<string, unknown>)[k];
    return v !== undefined && v !== null && String(v).trim() !== '';
  })));
  const markTouched = (field: string) => {
    touched.current.add(field);
  };

  useEffect(() => {
    const fields = preview?.fields;
    if (!fields) return;
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [field, value] of Object.entries(fields)) {
        if (value === null || value === undefined) continue;
        const current = (prev as Record<string, unknown>)[field];
        const hasCurrent =
          current !== undefined && current !== null && String(current).trim() !== '';
        if (hasCurrent) continue;
        if (touched.current.has(field) || initialKeys.current.has(field)) continue;
        (next as Record<string, unknown>)[field] = value;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [preview]);

  // Reset da proposta quando o cliente muda — evita FK inválida (proposta
  // de outro cliente ficando selecionada).
  const previousClient = useRef(values.clientId);
  useEffect(() => {
    if (previousClient.current !== values.clientId && previousClient.current) {
      setValues((prev) => ({ ...prev, proposalId: undefined }));
    }
    previousClient.current = values.clientId;
  }, [values.clientId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate(values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      if (errs.justificativaManual) setTab('manual');
      return;
    }
    onSubmit(values as CreateContractPayload);
  };

  const isManual = !values.proposalId?.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTab('identificacao')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'identificacao'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Identificação
        </button>
        {isManual && (
          <button
            type="button"
            onClick={() => setTab('manual')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'manual'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Contrato Manual
          </button>
        )}
      </div>

      {tab === 'identificacao' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              label="Número"
              value={values.numero ?? ''}
              onChange={(e) => set('numero', e.target.value)}
              error={errors.numero}
            />
            <TextInput
              label="Valor Total"
              type="number"
              step="0.01"
              min="0"
              value={values.valorTotal ?? ''}
              onChange={(e) => set('valorTotal', e.target.value || undefined)}
            />
          </div>

          <AutocompleteSelect
            label={isLoadingClientes ? 'Carregando clientes…' : 'Cliente'}
            options={clienteOptions}
            value={values.clientId ?? ''}
            onChange={(v) => set('clientId', v)}
            error={errors.clientId}
            disabled={isLoadingClientes}
          />

          <AutocompleteSelect
            label={!values.clientId ? 'Proposta vinculada (selecione o cliente primeiro)' : isLoadingPropostas ? 'Carregando propostas…' : 'Proposta vinculada (opcional)'}
            options={propostaOptions}
            value={values.proposalId ?? ''}
            onChange={(v) => set('proposalId', v || undefined)}
            disabled={!values.clientId || isLoadingPropostas}
          />

          {values.proposalId && (
            <ProposalOrigemBanner
              preview={preview}
              isLoading={isPreviewFetching}
              proposalCodigo={
                propostaOptions.find((o) => o.value === values.proposalId)?.label
              }
            />
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AutocompleteSelect
              label={isLoadingUsuarios ? 'Carregando…' : 'Responsável comercial'}
              options={usuarioOptions}
              value={values.responsavelComercialId ?? ''}
              onChange={(v) => set('responsavelComercialId', v)}
              error={errors.responsavelComercialId}
              disabled={isLoadingUsuarios}
            />
            <AutocompleteSelect
              label={isLoadingUsuarios ? 'Carregando…' : 'Gestor do contrato'}
              options={usuarioOptions}
              value={values.gestorContratoId ?? ''}
              onChange={(v) => set('gestorContratoId', v)}
              error={errors.gestorContratoId}
              disabled={isLoadingUsuarios}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <AutocompleteSelect
              label="Moeda"
              options={MOEDA_OPTIONS}
              value={values.moeda ?? 'BRL'}
              onChange={(v) => set('moeda', v)}
            />
            <TextInput
              label="Vigência Inicial"
              type="date"
              value={values.vigenciaInicial ?? ''}
              onChange={(e) => set('vigenciaInicial', e.target.value)}
              error={errors.vigenciaInicial}
            />
            <TextInput
              label="Vigência Final"
              type="date"
              value={values.vigenciaFinal ?? ''}
              onChange={(e) => set('vigenciaFinal', e.target.value)}
              error={errors.vigenciaFinal}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isRecorrente"
              checked={values.isRecorrente ?? false}
              onChange={(e) => set('isRecorrente', e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="isRecorrente" className="text-sm text-gray-700">Contrato Recorrente</label>
          </div>

          <Textarea
            label="Objeto"
            value={values.objeto ?? ''}
            onChange={(e) => set('objeto', e.target.value || undefined)}
            rows={3}
          />

          <Textarea
            label="Observações"
            value={values.observacoes ?? ''}
            onChange={(e) => set('observacoes', e.target.value || undefined)}
            rows={2}
          />
        </div>
      )}

      {tab === 'manual' && isManual && (
        <div>
          <Textarea
            label="Justificativa"
            value={values.justificativaManual ?? ''}
            onChange={(e) => set('justificativaManual', e.target.value)}
            rows={5}
            error={errors.justificativaManual}
            showCharCount
            minLength={20}
          />
          <p className="text-xs text-gray-500 mt-1">Obrigatória para contratos criados sem proposta (mínimo 20 caracteres).</p>
        </div>
      )}

      {externalError}

      <div className="flex justify-end pt-2">
        {preview && !preview.canGenerate && (
          <span className="self-center mr-3 text-xs text-amber-700">
            A API ainda aceitará a submissão — resolve os bloqueios acima antes de gerar.
          </span>
        )}
        <button
          type="submit"
          disabled={isSubmitting || (preview ? !preview.canGenerate : false)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition-colors"
        >
          {isSubmitting ? 'Salvando...' : submitLabel}
        </button>
      </div>
    </form>
  );
}
